/**
 * Bottle path geometry, one entry per bottle style the coffee card can draw.
 *
 * Each entry is generated from a shipped SVG asset in
 * `app/images/svg/rewards/`, not transcribed. Re-run the extraction if an
 * asset changes.
 *
 * Every style is drawn the same way: `outlinePath` is a hollow ring carrying
 * an outer and an inner contour, and `interiorPath` is that ring's inner
 * contour on its own. The interior IS the inside face of the glass, in the
 * same coordinate system as the outline, so it doubles as the clip region for
 * the liquid with no shift or rescale.
 *
 * WINE, from `wine_bottle_empty.svg` (viewBox 0 0 104.37 425.2).
 *
 * The companion `wine_bottle_filled.svg` is deliberately NOT used as the clip.
 * Measured bounding boxes in the outline's 104.37-wide viewBox:
 *
 *   outline, outer contour        x 0.00 -> 104.36   y max 425.21
 *   outline, inner contour        x 4.93 ->  99.42   y max 420.23
 *   filled asset, shifted 1.885   x 1.885 -> 102.50  y max 425.19
 *
 * The filled asset traces the bottle's OUTER body, so clipping with it let the
 * liquid spill over the wall and squared off the base. The wall is ~4.93
 * thick, not the 1.885 an even split of the width difference implies.
 *
 * BEER, from `beer_bottle_empty.svg` (viewBox 0 0 106.68 425.2).
 *
 * A longneck, traced off a reference silhouette rather than estimated: the
 * reference was decoded row by row and its width profile converted into the
 * vertex list the asset is generated from. Rendering the result at the
 * reference's own 566px height and comparing the two profiles row by row, no
 * row differs by more than 3px of 142 at the body.
 *
 * Drawn at the wine bottle's own 425.2 height, at the reference's 142:566
 * aspect, so a mixed row lines up on one baseline and the two styles are the
 * same size on the card. The 4.93 wall is the wine asset's, unscaled: the two
 * viewBoxes are the same height, so a unit is the same number of pixels in
 * both and the glass comes out equally thick.
 *
 * The inner contour is offset from a copy of the outline with the crown
 * finish removed. The lip and the transfer bead are about 1.5 units deep and
 * the wall is 4.93, so offsetting them would fold the contour through itself.
 * The bore runs straight from the mouth into the neck instead.
 */

export type BottleVariant = 'wine' | 'beer';

export type BottleGeometry = {
  viewBoxWidth: number;
  viewBoxHeight: number;
  /**
   * Baseline of the glass - liquid at this y reads as an empty bottle.
   *
   * Sits below the interior's own base on purpose: the liquid rectangle's top
   * edge is the surface, so starting it past the base guarantees the bottle
   * reads as empty with no sliver of liquid left in it.
   */
  floorY: number;
  /** Top of the liquid when the bottle is full. */
  fullY: number;
  /**
   * Height of the foam crown resting on the liquid, in viewBox units. Zero
   * for a style that has no head, which draws a bare liquid meniscus instead.
   */
  crownHeight: number;
  outlinePath: string;
  interiorPath: string;
};

export const BOTTLE_GEOMETRY: Record<BottleVariant, BottleGeometry> = {
  /**
   * `fullY` 110 sits at the base of the neck, where a real bottle's liquid
   * stops: the body reads as full and a short air gap is left in the neck.
   * Chosen over filling only to the shoulder (y=150), which left the whole
   * neck empty and read as a part-filled bottle rather than an earned one.
   */
  wine: {
    viewBoxWidth: 104.37,
    viewBoxHeight: 425.2,
    floorY: 425.2,
    fullY: 110,
    crownHeight: 0,
    outlinePath:
      'M62.06,425.2c-1.49,0-2.95,0-4.37-.02-3.66-.04-7.36-.04-11.02,0-12.24.12-28.22-.2-34.84-2.4-3.63-1.21-6.53-3.35-8.64-6.37-1.99-2.86-3.04-6.27-3.04-9.85-.06-35.98-.33-223.78,0-247.67.32-22.97,23.61-35.28,23.85-35.39.29-.15.66-.39,1.11-.72,3.89-2.95,6.22-7.68,6.21-12.66-.03-24.17-.08-93.49,0-102.99.04-4.78,5.93-7.11,17.99-7.11h5.73c12.06,0,17.94,2.33,17.99,7.11.08,8.81.04,68.31.01,103,0,4.98,2.32,9.71,6.22,12.66.54.41.89.61,1.09.71,6.54,3.28,23.58,16.03,23.85,35.39h0c.33,24.04.06,210.53,0,247.67,0,3.58-1.06,6.99-3.05,9.85-2.1,3.02-5.01,5.17-8.63,6.38-5.86,1.95-19.04,2.42-30.48,2.42ZM49.32,4.94c-8.99,0-12.68,1.53-13.06,2.26-.07,9.43-.02,78.72,0,102.89,0,6.52-3.05,12.73-8.18,16.61-.7.52-1.3.91-1.87,1.19-.21.1-20.84,11-21.12,31.04-.33,23.86-.06,211.62,0,247.59,0,2.58.75,5.01,2.16,7.04,1.48,2.12,3.54,3.64,6.14,4.5,4.59,1.53,16.7,2.32,33.23,2.15,3.69-.04,7.43-.04,11.12,0,16.53.17,28.64-.62,33.23-2.15,2.59-.86,4.66-2.38,6.13-4.51,1.41-2.03,2.16-4.47,2.16-7.04.06-37.13.33-223.59,0-247.59h0c-.27-20.04-20.91-30.94-21.12-31.04-.57-.29-1.18-.68-1.86-1.19-5.13-3.88-8.19-10.09-8.18-16.61.03-34.67.07-94.15-.01-102.94-.37-.69-4.06-2.21-13.04-2.21h-5.73Z',
    interiorPath:
      'M49.32,4.94c-8.99,0-12.68,1.53-13.06,2.26-.07,9.43-.02,78.72,0,102.89,0,6.52-3.05,12.73-8.18,16.61-.7.52-1.3.91-1.87,1.19-.21.1-20.84,11-21.12,31.04-.33,23.86-.06,211.62,0,247.59,0,2.58.75,5.01,2.16,7.04,1.48,2.12,3.54,3.64,6.14,4.5,4.59,1.53,16.7,2.32,33.23,2.15,3.69-.04,7.43-.04,11.12,0,16.53.17,28.64-.62,33.23-2.15,2.59-.86,4.66-2.38,6.13-4.51,1.41-2.03,2.16-4.47,2.16-7.04.06-37.13.33-223.59,0-247.59h0c-.27-20.04-20.91-30.94-21.12-31.04-.57-.29-1.18-.68-1.86-1.19-5.13-3.88-8.19-10.09-8.18-16.61.03-34.67.07-94.15-.01-102.94-.37-.69-4.06-2.21-13.04-2.21h-5.73Z',
  },

  /**
   * The two numbers are fixed by one rule: a beer's head has to top out level
   * with a wine bottle's liquid, so a mixed row has one fill line across it.
   *
   * The wine fills to y=110 and both styles share the 425.2-unit box, so the
   * crown's top is y=110 exactly. `fullY` 152 then rests the liquid at the
   * base of the neck, which leaves `crownHeight` 42 - a head that fills the
   * neck up to the line and no more.
   *
   * The liquid is as high as it can go without swallowing the head. Its crests
   * bulge about 8 units above the surface and float another 6, so they reach
   * y=138 at the top of the cycle; anything above about y=145 would leave the
   * head a sliver. Earlier pairs were all short of the line or thick with
   * foam: 184/48 topped out at y=136, 196/40 at y=156, and 184/74 hit the line
   * with a head 74 units deep.
   */
  beer: {
    viewBoxWidth: 106.68,
    viewBoxHeight: 425.2,
    floorY: 425.2,
    fullY: 152,
    crownHeight: 42,
    outlinePath:
      'M72.67,0A2,2 0 0 1 74.22,0.74L77.96,5.34A2.4,2.4 0 0 1 78.5,6.85L78.5,9.95A2.4,2.4 0 0 1 78.25,11.03L77.21,13.1A2,2 0 0 0 77,13.99L77,16.27A2,2 0 0 0 77.06,16.76L78.81,23.75A2.4,2.4 0 0 1 78.88,24.34L78.88,25.2A2.4,2.4 0 0 1 78.78,25.87L76.47,33.84A4,4 0 0 0 76.33,35.31L85.75,139.74A48,48 0 0 0 92.39,160.1L96.71,167.31A70.05,70.05 0 0 1 106.68,203.33L106.68,403.2A22,22 0 0 1 84.68,425.2L22,425.2A22,22 0 0 1 0,403.2L0,203.33A70.05,70.05 0 0 1 9.97,167.31L14.29,160.1A48,48 0 0 0 20.93,139.74L30.35,35.31A4,4 0 0 0 30.21,33.84L27.9,25.87A2.4,2.4 0 0 1 27.8,25.2L27.8,24.34A2.4,2.4 0 0 1 27.87,23.75L29.62,16.76A2,2 0 0 0 29.68,16.27L29.68,13.99A2,2 0 0 0 29.47,13.1L28.43,11.03A2.4,2.4 0 0 1 28.18,9.95L28.18,6.85A2.4,2.4 0 0 1 28.72,5.34L32.46,0.74A2,2 0 0 1 34.01,0L72.67,0ZM38,4.93A0.4,0.4 0 0 0 37.6,5.3L35.34,34.91A8.93,8.93 0 0 1 35.33,35.03L25.84,140.18A52.93,52.93 0 0 1 18.52,162.64L14.84,168.78A69.62,69.62 0 0 0 4.93,204.58L4.93,403.2A17.07,17.07 0 0 0 22,420.27L84.68,420.27A17.07,17.07 0 0 0 101.75,403.2L101.75,204.58A69.62,69.62 0 0 0 91.84,168.78L88.16,162.64A52.93,52.93 0 0 1 80.84,140.18L71.35,35.03A8.93,8.93 0 0 1 71.34,34.91L69.08,5.3A0.4,0.4 0 0 0 68.68,4.93L38,4.93Z',
    interiorPath:
      'M68.68,4.93A0.4,0.4 0 0 1 69.08,5.3L71.34,34.91A8.93,8.93 0 0 0 71.35,35.03L80.84,140.18A52.93,52.93 0 0 0 88.16,162.64L91.84,168.78A69.62,69.62 0 0 1 101.75,204.58L101.75,403.2A17.07,17.07 0 0 1 84.68,420.27L22,420.27A17.07,17.07 0 0 1 4.93,403.2L4.93,204.58A69.62,69.62 0 0 1 14.84,168.78L18.52,162.64A52.93,52.93 0 0 0 25.84,140.18L35.33,35.03A8.93,8.93 0 0 0 35.34,34.91L37.6,5.3A0.4,0.4 0 0 1 38,4.93L68.68,4.93Z',
  },
};
